import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from "@strivelabs/ui-core"
import { Loader2 } from "lucide-react"
import { PasswordInput } from "@/components/common"
import { PUBLISH_TARGETS, type PublishTargetName } from "@/constants/publishTargets"
import { useCreateBasicAuthConnection } from "@/hooks/useConnections"
import { BASE_URL, executeAppAction } from "@/services/DataService"
import { logger } from "@/lib/logger"
import type { App } from "@/services/DataService"

interface BasicAuthConnectModalProps {
  app: App | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

const formSchema = z.object({
  display_name: z
    .string()
    .min(1, "Connection name is required"),
  site_url: z
    .string()
    .min(1, "Site URL is required")
    .url("Enter a valid URL (e.g., https://blog.example.com)"),
  username: z
    .string()
    .min(1, "Username is required"),
  password: z
    .string()
    .min(1, "Password is required"),
})

type FormData = z.infer<typeof formSchema>

interface ValidatedUser {
  id: number | string
  name: string
  email?: string
}

export function BasicAuthConnectModal({ app, open, onOpenChange, onSuccess }: BasicAuthConnectModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const createConnection = useCreateBasicAuthConnection()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { display_name: "", site_url: "", username: "", password: "" },
  })

  // Reset form whenever the modal opens for a new app.
  useEffect(() => {
    if (open) {
      reset({
        display_name: app?.display_name ?? "",
        site_url: "",
        username: "",
        password: "",
      })
      setSubmitError(null)
    }
  }, [open, app?.display_name, reset])

  const onSubmit = async (form: FormData) => {
    if (!app) return

    const target = PUBLISH_TARGETS[app.short_name as PublishTargetName]
    const validationAction = target?.validationAction
    const trimmedSiteUrl = form.site_url.trim().replace(/\/+$/, "")
    const trimmedUsername = form.username.trim()
    // Don't trim the password — Application Passwords are exactly what the user
    // pastes, including any internal spaces. We only strip trailing whitespace
    // that pasting introduces.
    const trimmedPassword = form.password.replace(/^\s+|\s+$/g, "")

    setIsSubmitting(true)
    setSubmitError(null)
    try {
      let principalId: string | undefined
      let principalName: string | undefined
      let principalEmail: string | undefined

      if (validationAction) {
        const validationResponse = await executeAppAction(
          BASE_URL,
          app.short_name,
          validationAction,
          [],
          [{
            site_url: trimmedSiteUrl,
            username: trimmedUsername,
            password: trimmedPassword,
          }]
        )

        if (validationResponse?.status !== "success") {
          const message = validationResponse?.error?.message
            ?? validationResponse?.message
            ?? "We couldn't verify those credentials. Please double-check and try again."
          throw new Error(typeof message === "string" ? message : "Credential validation failed.")
        }

        const user = (validationResponse?.items?.[0]?.data ?? {}) as ValidatedUser
        if (user?.id !== undefined && user?.id !== null) {
          principalId = String(user.id)
        }
        principalName = user?.name
        principalEmail = user?.email
      }

      const authName = app.auth?.[0]?.short_name ?? "default"

      await createConnection.mutateAsync({
        appName: app.short_name,
        payload: {
          app_name: app.short_name,
          auth_name: authName,
          display_name: form.display_name,
          token_type: "basic",
          username: trimmedUsername,
          password: trimmedPassword,
          principal_id: principalId,
          principal_name: principalName,
          principal_email: principalEmail,
          custom_data: { site_url: trimmedSiteUrl },
        },
      })

      toast({
        title: "Connected",
        description: `${app.display_name} connected successfully.`,
      })
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      logger(error as Error, { context: "BasicAuthConnectModal: connect failed" })
      setSubmitError("We couldn't connect. Please check your credentials and try again.")
      toast({
        title: "Connection Failed",
        description: "We couldn't connect. Please check your credentials and try again.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!isSubmitting) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Connect {app?.display_name ?? ""}</DialogTitle>
          <DialogDescription>
            Provide your site URL and Application Password credentials. We&apos;ll verify them before saving.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="basic-auth-display-name">
              Connection name <sup className="text-red-500">*</sup>
            </Label>
            <Input
              id="basic-auth-display-name"
              placeholder="e.g., My Blog"
              disabled={isSubmitting}
              {...register("display_name")}
              className={errors.display_name ? "border-red-500" : ""}
            />
            {errors.display_name && (
              <p className="text-xs text-red-500">{errors.display_name.message}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="basic-auth-site-url">
              Site URL <sup className="text-red-500">*</sup>
            </Label>
            <Input
              id="basic-auth-site-url"
              placeholder="https://blog.example.com"
              disabled={isSubmitting}
              {...register("site_url")}
              className={errors.site_url ? "border-red-500" : ""}
            />
            {errors.site_url && (
              <p className="text-xs text-red-500">{errors.site_url.message}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="basic-auth-username">
              Username <sup className="text-red-500">*</sup>
            </Label>
            <Input
              id="basic-auth-username"
              placeholder="WordPress username or email"
              disabled={isSubmitting}
              {...register("username")}
              className={errors.username ? "border-red-500" : ""}
            />
            {errors.username && (
              <p className="text-xs text-red-500">{errors.username.message}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="basic-auth-password">
              Application password <sup className="text-red-500">*</sup>
            </Label>
            <PasswordInput
              id="basic-auth-password"
              placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
              autoComplete="off"
              spellCheck={false}
              disabled={isSubmitting}
              {...register("password")}
              className={errors.password ? "border-red-500" : ""}
            />
            {errors.password && (
              <p className="text-xs text-red-500">{errors.password.message}</p>
            )}
          </div>

          {submitError && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
              {submitError}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="black" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Connecting...
                </>
              ) : (
                "Connect"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
